import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Styles from './FinanceTipsAndTools.styled';
import Heading from '../../components/Heading/Heading';
import Posts from '../../components/Posts/Posts';
import { getPageContent } from '../../services/dpc';
import { getBlogPosts } from '../../services/blogService';
import getQueryParams from '../../util/getQueryParams';
import Error from '../../components/Error/Error';
import CTACard from '../../components/CTACard/CTACard';
import EmailSignup from '../../components/Shared/EmailSignup';
import PostsFilters from '../../components/Shared/PostsFilters';
import useGTM from '../../hooks/useGTM';
import theme from '../../styles/theme';

const parsePosts = posts =>
  posts
    ? posts.map(({ title, contentTag, category, slug, featuredMedia }) => ({
        title,
        contentTag,
        category,
        thumbnail: `${
          featuredMedia?.media?.fields?.file?.url ||
          featuredMedia?.thumbnail?.fields?.file?.url
        }?w=300`,
        mediaType: featuredMedia?.mediaType,
        slug,
      }))
    : [];

function FinanceTipsAndTools({
  data: {
    contentCTAs,
    contentVideos,
    faqPod,
    heroCta,
    heroMedia,
    heroText,
    mobileHeroMedia,
    metaDescription,
    preFooterCta,
    tipsAndToolsPod,
    titleTag,
    customerReviewPod,
    pageTitle,
    webForm,
    ...props
  } = {},
  blogData,
  params: { search = '', category = 'all' } = {},
  error,
  isLoading,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();
  const [page, setPage] = useState(1);
  const [searchError, setSearchError] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [posts, setPosts] = useState(parsePosts(blogData));
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters] = useState({
    search: search,
    category: category,
  });

  useEffect(() => {
    setPosts(parsePosts(blogData));
  }, [blogData]);

  const handleFilterUpdate = _filters => {
    const newFilters = {
      ...filters,
      ..._filters,
    };
    setFilters(newFilters);
    setIsSearching(true);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(
      setTimeout(async () => {
        triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Tips and Tools',
          category: 'loan_education',
          action: 'search',
          label: 'tips_tools',
        })();

        const _posts = await getBlogPosts(
          newFilters.category,
          newFilters.search,
          0,
          500,
          ['News']
        );

        if (_posts.status !== 200) {
          setPosts([]);
          setSearchError(_posts.statusText);
          setPage(1);
        } else {
          setPosts(parsePosts(_posts.data.result));
          setSearchError(false);
          setPage(1);
        }

        setIsSearching(false);
        setSearchTimeout(false);
      }, 300)
    );
  };

  const handleViewMoreClick = e => {
    e.preventDefault();
    e.stopPropagation();

    setPage(page + 1);

    triggerGTMEvent({
      name: 'vmf.cta_click',
      location: 'Tips and Tools',
      category: 'loan_education',
      action: 'button',
      label: 'tips_tools_all',
    })();
  };

  const onEmailSubmit = () => {
    triggerGTMEvent({
      name: 'vmf.cta_click',
      location: 'Tips and Tools',
      category: 'contact_us',
      action: 'form',
      label: 'email_form_submit',
    })();
  };

  return (
    <Layout isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />
      <Styles>
        <Heading background={theme.colors.blue10} appearance='dark'>
          <h1>{pageTitle}</h1>
        </Heading>

        <PostsFilters
          updateFilter={handleFilterUpdate}
          filters={filters}
          isSearching={isSearching}
        />

        {posts.length === 0 ? (
          <h2 className='search-error'>
            We couldn't find any relevant articles, please try a different
            search.
          </h2>
        ) : (
          <Posts
            posts={posts.slice(0, 16 * page)}
            link={
              posts.length < 16 * page
                ? false
                : { url: '', title: 'Load More', onClick: handleViewMoreClick }
            }
            isLeftAligned={true}
          />
        )}

        <CTACard
          form={
            <EmailSignup
              buttonLabel={webForm?.[0]?.firstFormButtonText}
              originCampaign={webForm?.[0]?.originCampaign}
              originWebApp={webForm?.[0]?.originWebApp}
              originWebFormCategory={webForm?.[0]?.originWebFormCategory}
              originWebFormType={webForm?.[0]?.originWebFormType}
              originWebPageType={webForm?.[0]?.originWebPageType}
              dataExtension={webForm?.[0]?.dataExtension}
              onEmailSubmit={onEmailSubmit}
            />
          }
          disclaimer={
            <ReactMarkdown
              source={webForm?.[0]?.disclaimer}
              renderers={{
                paragraph: ({ children }) => (
                  <p className='type-caption bold color-grey1'>{children}</p>
                ),
              }}
            />
          }
          divider={false}
        >
          <p className='type-label-large bold'>{webForm?.[0]?.formIntro}</p>
          <h2 className='type-h1'>{webForm?.[0]?.formHeader}</h2>
        </CTACard>
      </Styles>
    </Layout>
  );
}

FinanceTipsAndTools.getInitialProps = async ({ req = false }) => {
  try {
    const queryData = getQueryParams(
      (req ? req?._parsedUrl?.search : window.location.search) || ''
    );
    const data = await getPageContent('/resources/blog/');
    const blogData = await getBlogPosts(
      queryData?.category || 'all',
      queryData?.search || '',
      0,
      500,
      ['News']
    );

    if (data.status !== 200) {
      throw data.statusText;
    }

    if (blogData.status !== 200) {
      throw blogData.statusText;
    }

    return {
      data: data.data.result,
      blogData: blogData.data.result,
      params: {
        search: queryData?.search,
        category: queryData?.category,
      },
    };
  } catch (error) {
    return {
      error,
    };
  }
};

FinanceTipsAndTools.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default FinanceTipsAndTools;
